import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@kk/ui/components/Button';
import Input from '@kk/ui/components/Input';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { useConnectUser } from '@/api/hooks/useAuthorize';
import { logout } from '@/auth';

const loginSchema = z.object({
  username: z.string().min(1, 'Brugernavn er påkrævet'),
  password: z.string().min(1, 'Adgangskode er påkrævet'),
});

type LoginSchema = z.infer<typeof loginSchema>;

export default function LoginConnectUserPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const methods = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
  });

  const connectUser = useConnectUser(uuid, {
    onSuccess: () => {
      navigate('/');
    },
  });

  const signoutUser = () => {
    logout();
  };

  const onSubmit = (data: LoginSchema) => {
    if (!uuid) return;
    connectUser.mutate(data);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className="mx-auto mt-40 w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-8 shadow-lg"
      >
        <h2 className="mb-6 text-center text-2xl font-semibold text-blue-900">
          {t('common.connectToMitID')}
        </h2>

        <div className="flex flex-col gap-4">
          <Input
            label={t('common.username')}
            {...methods.register('username')}
            error={methods.formState.errors.username}
          />

          <Input
            type="password"
            label={t('common.password')}
            {...methods.register('password')}
            error={methods.formState.errors.password}
          />

          <Button
            type="submit"
            theme="primary"
            disabled={connectUser.isPending}
            className="mt-4 w-full flex justify-center"
          >
            {connectUser.isPending
              ? t('common.connecting')
              : t('common.connect')}
          </Button>
          <Button
            type="button"
            theme="secondary"
            onClick={signoutUser}
            className="w-full flex justify-center"
          >
            {t('button.cancel')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
